import ValidatedField from "@/validation/ValidatedField";
import Log from "@/utils/Log";
import RpcInputValidationException from "@/server/com/silkplm/engine/rpc/exceptions/RpcInputValidationException";
import MD5 from "crypto-js/md5";

import {cloneClassInstance, stringifyForCompare} from "@/utils/Objects";
import FormShadowFieldConfig from "@/server/com/silkplm/engine/forms/schema/definition/FormShadowFieldConfig";

export default class ValidatedForm {
    constructor() {
        this.fakeModel = false;

        this.generalFeedback = "";
        this.isValid = true;

        this._unsavedChanges = false;
        this._initialChecksum = null;

        this._canSubmit = true;
        /**
         * @type {null|Function}
         */
        this.canSubmitMonitor = null;
        this.mode = "NEW";
        this.originalSource = null;

        this.onChangeCallback = null;

        this.onChangeHandlers = [];
        this.onChangePreValidateHandlers = [];
        this.onLoadedHandlers = [];

        this.initialized = false;
    }


    onLoaded(callback) {
        this.onLoadedHandlers.push(callback);
    }

    onChange(callback) {
        this.onChangeHandlers.push(callback);
    }

    onChangePreValidate(callback) {
        this.onChangePreValidateHandlers.push(callback);
    }

    emitOnChangePreValidate() {
        this.onChangePreValidateHandlers.forEach(callback => {
            try {
                callback()
            } catch (e) {
                Log.fatal(e, true);
            }
        });
    }

    emitOnChange() {
        this.onChangeHandlers.forEach(callback => {
            try {
                callback()
            } catch (e) {
                Log.fatal(e, true);
            }
        });
    }

    emitOnLoaded(inst) {
        this.onLoadedHandlers.forEach(callback => {
            try {
                callback(inst)
            } catch (e) {
                Log.fatal(e, true);
            }
        });
    }

    set initialChecksum(val) {
        if (val == null) {
        }
        this._initialChecksum = val;
    }

    get initialChecksum() {
        return this._initialChecksum;
    }

    set canSubmit(val) {
        // if (!!this._canSubmit !== !!val) {
        //     console.log("Chg");
        // }

        this._canSubmit = val;
        if (this.canSubmitMonitor) {
            this.canSubmitMonitor(val);
        }
        // console.log("CS:" + this._canSubmit);
    }

    get canSubmit() {
        return this._canSubmit;
    }

    set unsavedChanges(val) {
        // if (val) {
        //     console.log('lol');
        // }
        this._unsavedChanges = val;
    }

    get unsavedChanges() {
        return this._unsavedChanges;
    }

    get pendingTranslations() {
        return this.originalSource?.__supplement__?.translationInProgress;
    }


    resetChecksum() {
        this.unsavedChanges = false;
        this.initialChecksum = this.calcChecksum();
        if (this.onChangeCallback) {
            this.onChangeCallback(this);
        }
    }

    /**
     * @param editedObject
     * @param {string=} typeOfEditMode
     */
    editMode(editedObject, typeOfEditMode) {
        this.originalSource = editedObject;
        this.initialized = false;
        if (typeOfEditMode?.length) {
            this.setMode(typeOfEditMode);
        } else {
            this.setMode("EDIT");
        }
        this.fillValuesFrom(editedObject);
        this.initialChecksum = this.calcChecksum();
        this.unsavedChanges = false;

        this.validate().then();
        this.initialized = true;
        if (this.onChangeCallback) {
            this.onChangeCallback(this);
        }
        this.emitOnLoaded(editedObject)
    }

    markUnchanged() {
        this.initialChecksum = this.calcChecksum();
        this.unsavedChanges = false;
    }

    /**
     * @param editedObject
     */
    createMode(editedObject) {
        this.setDefaultValues();
        this.setMode("NEW");
        if (editedObject) {
            this.fillValuesFrom(editedObject);
        }
        this.resetChecksum();
        this.validate().then();
        this.initialized = true;
        if (this.onChangeCallback) {
            this.onChangeCallback(this);
        }
        this.emitOnLoaded(editedObject)
    }

    hasValidatedFieldByName(name) {
        return this.hasOwnProperty(name);
    }

    validatedFieldByName(name) {
        return this[name];
    }

    /**
     * @param {RpcInputValidationException|RpcException} validationException
     */
    parseException(validationException) {
        this.cleanAllErrors();
        this.isValid = false;

        if (validationException instanceof RpcInputValidationException) {
            for (let f in validationException.fields) {
                if (this.hasValidatedFieldByName(f)) {
                    if (this.validatedFieldByName(f) instanceof ValidatedField) {
                        this.validatedFieldByName(f).parseException(validationException.fields[f]);
                    }
                } else {
                    Log.info("Field '" + f + "' not found in form");
                }
            }
            this.generalFeedback = validationException.errorMessage;
        } else {
            this.generalFeedback = "Server error - please try again in few minutes";
        }
        if (this.generalFeedback) {
            throw validationException;
        }
    }

    /**
     * @param {InputValidationResult}  validationResult
     */
    extractFieldErrors(validationResult) {
        this._forEachValidatedField((fieldName, fieldInstance) => {
            if (validationResult.fieldsValidationResults.hasOwnProperty(fieldName)) {
                fieldInstance.parseException(validationResult.fieldsValidationResults[fieldName]);
            } else {
                fieldInstance.crossFieldIsValid = true;
                fieldInstance.crossFieldErrorMessage = "";
            }
        });
        this.generalFeedback = validationResult.errorMessage;
        this.refreshValidationState();
        this.isValid = this.isValid && validationResult.isValid;
    }

    cleanAllErrors() {
        this.isValid = true;
        this.generalFeedback = true;
        this._forEachValidatedField((fieldName, fieldInstance) => {
            fieldInstance.crossFieldIsValid = true;
            fieldInstance.crossFieldErrorMessage = "";
            fieldInstance.fieldIsValid = true;
            fieldInstance.fieldErrorMessage = "";
        });
    }

    refreshValidationState() {
        if (this.refreshValidationStateTimer) {
            return;
        }
        this.refreshValidationStateTimer = setTimeout(() => {
            this.refreshValidationStateTimer = null;


            if (this.initialized) {
                this.emitOnChangePreValidate();
            }

            let isValid = true;
            let canSubmit = true;
            this._forEachValidatedField((name, field) => {
                if (!field?.formFieldConfig ||
                    field.formFieldConfig instanceof FormShadowFieldConfig ||
                    field?.formFieldConfig?.renderer === 'RENDERER_TEXT_READ_ONLY' ||
                    field?.formFieldConfig?.computed) {
                    return;
                }
                isValid = isValid && field.isValid;
                // TODO wojtek - weryfikacja który field jest isValid - pomocne w weryfikacji
                if (!field.isValid || !field.isValid) {
                    let x = 5;
                }
                let xcanSubmit = canSubmit;
                canSubmit = isValid && canSubmit && !field.validationPending() && field.isValid;
                if (xcanSubmit && !canSubmit) {
                    let x = 5;
                    field.validationPending(true);
                }
            });

            this.isValid = isValid;
            this.canSubmit = canSubmit;
            if (this.initialChecksum != null) {
                this.unsavedChanges = (this.initialChecksum !== this.calcChecksum());
            }
            if (this.onChangeCallback) {
                this.onChangeCallback(this);
            }
        })

    }

    /**
     * @param {string} mode
     */
    setMode(mode) {
        this.mode = mode;
        this._forEachValidatedField((name, field) => {
            field.mode = mode;
        });
    }


    /**
     * @param source
     */
    fillValuesFrom(source) {
        this.originalSource = source;
        this._forEachValidatedField((name, field) => {
            if (source.hasOwnProperty(name)) {
                // console.log("name: " + name + " " + source["methodsPreservingTextiles"])
                if (name === "methodsPreservingTextiles" || name === "contactWithFood") {
                    // console.log(source[name]);
                }
                if (field.formFieldConfig?.translations || field.formFieldConfig?.interfaceTranslation) {
                    field.setInitialValueWithTranslations(source[name], source['_translations'], name);
                } else {
                    field.setInitialValue?.(source[name], null, name);
                    field.resetInitialValue?.(source[name]);
                    field.value = source[name];
                }
            } else {
                let descriptor = Object.getOwnPropertyDescriptor(source, name) ?? Object.getOwnPropertyDescriptor(Object.getPrototypeOf(source), name);
                if (descriptor?.get) {
                    let val = source[name];
                    if (field.formFieldConfig?.translations || field.formFieldConfig?.interfaceTranslation) {
                        field.setInitialValueWithTranslations(source[name], source['_translations'], name);
                    } else {
                        field.setInitialValue?.(val, null, name);
                        field.resetInitialValue?.(val);
                        field.value = source[name];
                    }
                }
            }
        });
    }

    isAutoSave(fieldName, validatedField) {
        return false;
    }

    /**
     * @param source
     */
    calcChecksum() {
        let collection = {};

        this._forEachValidatedField((name, field) => {
            if (this.isAutoSave(name, field)) {
                return;
            }
            if (field.formFieldConfig?.computed) {
                return;
            }
            if (field.value === "" || field.value === null || field.value === undefined) {
                collection[name] = "";
            } else {
                collection[name] = field.value;
            }
        });
        // wypisac collection, (przed zapisem i po zapisie inny wynik)
        // console.log(stringifyForCompare(collection));
        return stringifyForCompare(collection);
        // return MD5(stringifyForCompare(collection)).toString();

    }

    /**
     * @param source
     */
    setDefaultValues() {
        this.generalFeedback = "";
        this.isValid = true;
    }

    /**
     * @param target
     * @return {*}
     */
    populateValuesTo(target) {
        this._forEachValidatedField((name, field) => {
            if (target.hasOwnProperty(name)) {
                target[name] = field.value;
            }
        });
        return target;
    }

    /**
     * @param {function(string, ValidatedField)} callback
     * @private
     */
    _forEachValidatedField(callback) {
        let results = [];
        for (let k in this) {
            if (this[k] instanceof ValidatedField) {
                results.push(callback(k, this[k]));
            }
        }
        return results;
    }

    async validate(force = false) {
        if (force) {
            this.unsavedChanges = true;
            this.initialChecksum = "";
            this._forEachValidatedField((name, field) => {
                field._stringifiedinitialValue = null;
            });
        }

        let promises = [];
        this._forEachValidatedField((name, field) => {
            promises.push(field.validate());
        });
        return Promise.all(promises).then(() => {
            this.refreshValidationState();
        });
        let x = 5;
    }

    setLabels() {
        this._forEachValidatedField((name, field) => {
            if (field.label == null) {
                //kebab-case to camelCase
                if (name.includes("-") || name.includes("_")) {
                    let arr;
                    if (name.includes("-"))
                        arr = name.split('-');
                    else if (name.includes("_"))
                        name.includes("_");
                    let capital = arr.map((item, index) => index ? item.charAt(0).toUpperCase() + item.slice(1).toLowerCase() : item.toLowerCase());
                    name = capital.join("");
                }
                //camelCase to normal string
                name = name.replace(/([A-Z])/g, ' $1');
                name = name.charAt(0).toUpperCase() + name.slice(1);
                field.label = name;
            }
        });

    }
}
